import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  TemplateRef,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder, Validators } from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { isEmptyHtml } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { InterviewResultsActions } from '@core/interview-results-data';
import { InterviewResult } from '@core/interview-results-types';
import type { PlacementInfo } from '@core/offers-ui';
import {
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogService,
  DividerComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  RtStatefulComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { InterviewKind } from '../interview-kind';
import { InterviewSummaryComponent } from '../interview-summary/interview-summary.component';
import {
  EvaluationFormState,
  QuickEvaluationCacheService,
} from '../quick-evaluation-cache.service';

type State = {
  loading: boolean;
};

@Component({
  selector: 'rt-quick-evaluation-form',
  templateUrl: 'quick-evaluation-form.component.html',
  styleUrls: ['./quick-evaluation-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    NgTemplateOutlet,
    HeadingComponent,
    GroupComponent,
    IconButtonComponent,
    DividerComponent,
    InterviewSummaryComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    IconComponent,
    TranslatePipe,
  ],
})
export class QuickEvaluationFormComponent extends RtStatefulComponent<State> {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly i18n = inject(I18nService);
  private readonly dialogSvc = inject(DialogService);
  private readonly viewRef = inject(ViewContainerRef);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly quickEvaluationCache = inject(QuickEvaluationCacheService);

  protected readonly cdRef: ChangeDetectorRef;

  public readonly result = input<InterviewResult>(undefined!);
  public readonly assignedPlacement = input<PlacementInfo | null>();
  public readonly headerTpl = input<TemplateRef<any>>(undefined!);

  public close = output<void>();

  public form: UntypedFormGroup;

  private ngOnDestroy$ = new Subject<void>();

  protected get cachedEvaluationForm(): EvaluationFormState | null {
    const result = this.result();
    if (result?.id === null) {
      return null;
    }

    return this.quickEvaluationCache.getValue(result.id);
  }

  private set cachedEvaluationForm(formValue: EvaluationFormState) {
    this.quickEvaluationCache.setValue(this.result().id, formValue);
  }

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      loading: false,
    });
  }

  public ngOnChanges(): void {
    if (this.result() && !this.form) {
      this.createEditForm();
      this.subscribeToEvents();
    }
  }

  public ngOnInit(): void {
    this.form
      .get('ratingNote')
      ?.valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(_ => this.cdRef.detectChanges());
    this.form.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(value => (this.cachedEvaluationForm = value));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public cancel(): void {
    this.quickEvaluationCache.deleteValue(this.result().id);
    this.close.emit();
  }

  public clearEmptyHtmlAndSave(): void {
    this.setState({
      loading: true,
    });

    this.clearEmptyHtml();
    const { createdAt, ...value } = this.form.getRawValue();
    this.store.dispatch(
      InterviewResultsActions.update({
        payload: {
          data: {
            id: this.result().id,
            value: { interviewResult: value },
          },
        },
      }),
    );
  }

  public delete(): void {
    const message = this.i18n.translate('app.interview.delete_message');
    const title = this.i18n.translate('app.interview.delete_title');

    this.openConfirmDialog(message, title, 'error').subscribe(confirmed => {
      if (confirmed) {
        this.deleteInterviewResult();
      }
    });
  }

  public onRateChange(): void {
    this.form.markAsDirty();
    this.cdRef.detectChanges();
  }

  private clearEmptyHtml(): void {
    const ratingNote: string = this.form.value.ratingNote ?? '';

    this.form.patchValue({
      ratingNote: isEmptyHtml(ratingNote) ? '' : ratingNote,
    });
  }

  private createEditForm(): void {
    this.form = this.formBuilder.group(this.initialState());
    const cached = this.cachedEvaluationForm;

    if (!cached) {
      return;
    }

    this.restoreFormState(cached);
  }

  private openConfirmDialog(message: string, title: string, variant: string): Observable<boolean> {
    return this.dialogSvc.confirm(
      { title, message, variant, isCancelable: true, safeFooter: true },
      this.viewRef,
    );
  }

  private deleteInterviewResult(): void {
    this.setState({
      loading: true,
    });

    this.store.dispatch(
      InterviewResultsActions.deleteOnePessimistic({
        payload: {
          data: {
            id: this.result().id,
          },
          correlationId: generateUuid(),
        },
      }),
    );
  }

  private subscribeToEvents(): void {
    this.actions$
      .pipe(
        ofType(
          InterviewResultsActions.deleteOnePessimisticSuccess,
          InterviewResultsActions.updateSuccess,
        ),
      )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(action => {
        this.setState({
          loading: false,
        });
        this.close.emit();
        const result = this.result();
        if (action.payload.data.id === result.id) {
          this.quickEvaluationCache.deleteValue(result.id);
        }
      });

    this.actions$
      .pipe(
        ofType(
          InterviewResultsActions.deleteOnePessimisticFailed,
          InterviewResultsActions.updateFailed,
        ),
      )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.setState({
          loading: false,
        });
      });
  }

  private initialState() {
    const result = this.result();
    return {
      kind: this.result()?.kind || InterviewKind.QUICK,
      rating: [this.result()?.rating || '', Validators.required],
      ratingNote: [this.result()?.ratingNote || ''],
      offerId: this.result()?.offerId || '',
      stageId: this.result()?.stageId || '',
      createdAt: result?.updatedAt || result?.createdAt || '',
    };
  }

  private restoreFormState(cached: EvaluationFormState): void {
    if (!this.result() || this.isDatabaseStateNewer(cached)) {
      return;
    }

    this.form.patchValue(cached);
  }

  private isDatabaseStateNewer(cached: EvaluationFormState): boolean {
    const result = this.result();
    const databaseEpoch = new Date(result.updatedAt || result.createdAt).getTime();
    const cachedEpoch = new Date(cached.createdAt).getTime();
    return databaseEpoch > cachedEpoch;
  }
}
